import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../Button';
import LanguageSwitcher from '../LanguageSwitcher';
import { scrollToElement } from '../../utils/helpers';
import { localizePath, splitLanguageFromPath } from '../../utils/language';
import './Header.css';

const Header = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = splitLanguageFromPath(location.pathname).path === '/';
    const homePath = localizePath(i18n.language, '/');
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { key: 'home', label: t('nav.home'), href: '#hero' },
        { key: 'about', label: t('nav.about'), href: '#insoles' },
        { key: 'services', label: t('nav.services'), href: '#products' },
        { key: 'faq', label: t('nav.faq'), href: '#faq' },
        { key: 'contacts', label: t('nav.contacts'), href: '#contacts' },
    ];

    const contactItem = navItems.find((item) => item.key === 'contacts');
    const desktopNavItems = navItems.filter((item) => item.key !== 'contacts');

    const handleNavClick = (e, href) => {
        e.preventDefault();
        const id = href.replace('#', '');
        if (isHomePage) {
            scrollToElement(id);
        } else {
            navigate({ pathname: homePath, hash: `#${id}` });
        }
        setIsMobileMenuOpen(false);
    };

    const handleLogoClick = (e) => {
        if (isHomePage) {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleCtaClick = () => {
        if (isHomePage) {
            scrollToElement('contact-form');
        } else {
            navigate({ pathname: homePath, hash: '#contact-form' });
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
            <div className="header-inner">
                {/* Left: Contact Administrator */}
                <div className="header-left">
                    {contactItem && (
                        <a
                            href={contactItem.href}
                            className="header-contact"
                            onClick={(e) => handleNavClick(e, contactItem.href)}
                        >
                            <span className="header-contact-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" role="img" focusable="false">
                                    <path d="M12 2a6 6 0 0 0-6 6v1.2a5 5 0 0 0-3 4.8v3a3 3 0 0 0 3 3h1a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H6.1A3.2 3.2 0 0 1 9 8a3 3 0 1 1 6 0 3.2 3.2 0 0 1 2.9 4H17a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h1a3 3 0 0 0 3-3v-3a5 5 0 0 0-3-4.8V8a6 6 0 0 0-6-6Z" />
                                </svg>
                            </span>
                            <span className="header-contact-text">
                                <span className="header-contact-title">{t('nav.contact_admin_title')}</span>
                                <span className="header-contact-line">{t('nav.contact_admin_department')}</span>
                                <span className="header-contact-line">{t('nav.contact_admin_hours')}</span>
                                <span className="header-contact-line">{t('nav.contact_admin_note')}</span>
                            </span>
                        </a>
                    )}
                </div>

                {/* Center: Logo */}
                <div className="header-center">
                    <Link to={homePath} className="header-logo" onClick={handleLogoClick}>
                        <img src="/logo.jpg" alt="Logo" />
                    </Link>
                </div>

                {/* Right: Links + Actions */}
                <div className="header-right">
                    <nav className="header-nav">
                        <ul className="nav-list">
                            {desktopNavItems.map((item) => (
                                <li key={item.key}>
                                    <a
                                        href={item.href}
                                        className="nav-link"
                                        onClick={(e) => handleNavClick(e, item.href)}
                                    >
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div className="header-actions">
                        <LanguageSwitcher />
                    </div>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <nav className="mobile-nav">
                            <ul className="mobile-nav-list">
                                {navItems.map((item) => (
                                    <li key={item.key}>
                                        <a
                                            href={item.href}
                                            className="mobile-nav-link"
                                            onClick={(e) => handleNavClick(e, item.href)}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <div className="mobile-menu-footer">
                                <LanguageSwitcher />
                                <Button
                                    variant="primary"
                                    fullWidth
                                    onClick={handleCtaClick}
                                >
                                    {t('nav.book')}
                                </Button>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
